<template>
    <v-container fluid class="dashboard-page">
        <GemaWorksCheckDialog />
        <GemaSoundfileDialog />
        <v-row align="start">
            <!-- Column 1: Tools -->
            <v-col cols="4">
                <!-- Quicklinks -->
                <v-card elevation="0" class="mb-3 quicklinks-card">
                    <v-card-title>{{ $t('lang_DASHBOARD_CAPTION_QUICKLINKS') }}</v-card-title>
                    <v-card-subtitle>{{ $t('lang_DASHBOARD_CAPTION_QUICKLINKS_TEXT') }}</v-card-subtitle>
                    <v-card-text>
                        <v-select
                            v-model="selectedQuicklinksGuide"
                            :items="quicklinksGuidesItems"
                            item-text="text"
                            item-value="value"
                            :label="quicklinksGuidesLabel"
                            :placeholder="$t('lang_DASHBOARD_CAPTION_QUICKLINKS_GUIDES_PLACEHOLDER')"
                            persistent-placeholder
                            dense
                            clearable
                            :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                            :menu-props="{ contentClass: 'quicklinks-template-menu' }"
                            @change="onQuicklinksGuideSelected"
                            :alvqa="alvqa + '-dropdown-quicklinks-guides'"
                        ></v-select>
                        <v-select
                            v-model="selectedQuicklinksTemplate"
                            :items="quicklinksTemplatesItems"
                            item-text="text"
                            item-value="value"
                            :label="quicklinksTemplatesLabel"
                            :placeholder="$t('lang_DASHBOARD_CAPTION_QUICKLINKS_TEMPLATES_PLACEHOLDER')"
                            persistent-placeholder
                            dense
                            clearable
                            :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                            :menu-props="{ contentClass: 'quicklinks-template-menu' }"
                            @change="onQuicklinksTemplateSelected"
                            :alvqa="alvqa + '-dropdown-quicklinks-templates'"
                        ></v-select>
                    </v-card-text>
                </v-card>

                <!-- User Meeting (Calendly) -->
                <v-card elevation="0" class="mb-3">
                    <img
                        :src="require('@/assets/default_picture/avatar/avatar-pt-120.png')"
                        class="quicklinks-avatar"
                        :alvqa="alvqa + '-image-user-meeting-icon'"
                    />
                    <v-card-title>{{ $t('lang_DASHBOARD_CAPTION_USER_MEETING') }}</v-card-title>
                    <v-card-subtitle>{{ $t('lang_DASHBOARD_CAPTION_USER_MEETING_TEXT') }}</v-card-subtitle>
                    <v-card-actions class="pl-4">
                        <BaseButton
                            @click="openUserMeeting"
                            label="lang_DASHBOARD_CAPTION_BUTTON_USER_MEETING"
                            color="buttonNewEntity"
                            :alvqa="alvqa + '-button-user-meeting'"
                        />
                    </v-card-actions>
                </v-card>

                <!-- GEMA Status Check -->
                <v-card elevation="0" class="mb-3">
                    <v-card-title>{{ $t('lang_DASHBOARD_CAPTION_GEMA_STATUS_CHECK') }}</v-card-title>
                    <v-card-subtitle>{{ $t('lang_DASHBOARD_CAPTION_GEMA_STATUS_CHECK_SUBTITLE') }}</v-card-subtitle>
                    <v-card-actions class="pl-4">
                        <BaseButton
                            @click="showGemaWorksCheckDialog = !showGemaWorksCheckDialog" 
                            label="lang_DASHBOARD_CAPTION_BUTTON_STATUS_CHECK"
                            :alvqa="alvqa +  '-button-gema-status-check'"
                        />
                    </v-card-actions>
                </v-card>

                <!-- TAG Export -->
                <v-card elevation="0" class="mb-3">
                    <v-card-title>{{ $t('lang_DASHBOARD_CAPTION_TAG_EXPORT') }}</v-card-title>
                    <v-card-subtitle>{{ $t('lang_DASHBOARD_CAPTION_TAG_EXPORT_TEXT') }}</v-card-subtitle>
                    <v-card-text>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="SelectedTagExportDomain"
                            :items="tagExportDomainItems" 
                            :label="$t('lang_GENERIC_CAPTION_DOMAINS')"
                            :withMargin="false"
                            :clearable="false"
                            :alvqa="alvqa +  '-dropdown-tag-export-domain'"
                        />
                        <BaseButton
                            @click="launchTagsExportByDomain" 
                            label="lang_GENERIC_CAPTION_EXPORT"
                            :alvqa="alvqa +  '-button-tag-export'"
                        />
                    </v-card-text>
                </v-card>

                <!-- CSV Conversion tools -->
                <v-card elevation="0" class="mb-3">
                    <v-card-title>{{ $t('lang_DASHBOARD_CAPTION_CSV_CONVERSION_TOOLS') }}</v-card-title>
                    <v-card-subtitle>{{ $t('lang_DASHBOARD_CAPTION_CSV_CONVERSION_TOOLS_TEXT') }}</v-card-subtitle>
                    <v-card-text>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="SelectedCsvConversionTool"
                            :items="csvConversionToolsItems" 
                            :label="$t('lang_DASHBOARD_CAPTION_CSV_CONVERSION_TOOLS_SELECT')"
                            :withMargin="false"
                            :clearable="false"
                            :alvqa="alvqa +  '-dropdown-tag-export-domain'"
                        />
                        <v-file-input
                            v-model="fileObj"
                            :label="$t('lang_GENERAL_CAPTION_SELECT_FILE')"
                            clearable
                            @change="submitFile"
                            :accept="getAcceptedFileTypes"
                            :disabled="!selectedToolConfig"
                        />
                        <BaseButton
                            :disabled="!filePath || csvConversionInProgress"
                            @click="launchCsvConversionTool"
                            label="lang_GENERIC_CAPTION_CONVERT"
                        />
                        <v-progress-circular
                            v-if="csvConversionInProgress"
                            size="24"
                            width="3"
                            color="primary"
                            indeterminate
                            class="ml-2"
                        />
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Column 2: Content -->
            <v-col cols="5">
                <!-- News -->
                <v-card elevation="0" class="mb-3" style="background-color: #F2EDDA;">
                    <v-card-title>{{ $t('lang_DASHBOARD_CAPTION_CONTENT_NEWS') }}</v-card-title>
                    
                    <!-- Loading state -->
                    <template v-if="isLoadingInsights">
                        <v-card-subtitle>{{ $t('lang_GENERIC_CAPTION_LOADING') }}</v-card-subtitle>
                    </template>
                    
                    <!-- Show content if available -->
                    <template v-else-if="storyblokContent">
                        <v-card-subtitle 
                            :style="getHeadlineStyle(storyblokContent.content.Priority)"
                        >{{ storyblokContent.content.Headline }}</v-card-subtitle>
                        <v-card-text>
                            <v-img 
                                v-if="storyblokContent.content.Image && storyblokContent.content.Image.filename"
                                :src="storyblokContent.content.Image.filename"
                                :alt="storyblokContent.content.Image.alt || storyblokContent.content.Headline"
                                max-width="100%"
                                class="mb-4 clickable-image"
                                @click="showImageDialog = true"
                            />
                            <div v-html="renderRichText(storyblokContent.content.Paragraph)"></div>
                        </v-card-text>
                    </template>
                    
                    <!-- No interests configured - show specific Storyblok story if available -->
                    <template v-else-if="showNoInterestsMessage">
                        <template v-if="noInterestsStoryContent">
                            <v-card-subtitle 
                                :style="getHeadlineStyle(noInterestsStoryContent.content.Priority)"
                            >{{ noInterestsStoryContent.content.Headline }}</v-card-subtitle>
                            <v-card-text>
                                <v-img 
                                    v-if="noInterestsStoryContent.content.Image && noInterestsStoryContent.content.Image.filename"
                                    :src="noInterestsStoryContent.content.Image.filename"
                                    :alt="noInterestsStoryContent.content.Image.alt || noInterestsStoryContent.content.Headline"
                                    max-width="100%"
                                    class="mb-4 clickable-image"
                                    @click="showImageDialog = true"
                                />
                                <div v-html="renderRichText(noInterestsStoryContent.content.Paragraph)"></div>
                            </v-card-text>
                        </template>
                        <template v-else>
                            <v-card-subtitle>{{ $t('lang_DASHBOARD_CAPTION_CONTENT_NEWS_SET_INTERESTS') }}</v-card-subtitle>
                        </template>
                    </template>
                    
                    <!-- No content available in this language -->
                    <template v-else>
                        <v-card-subtitle>{{ $t('lang_DASHBOARD_CAPTION_CONTENT_NEWS_NO_CONTENT_IN_LANGUAGE') }}</v-card-subtitle>
                    </template>
                    
                    <!-- Link to all news -->
                    <v-card-actions class="pt-0 pl-4">
                        <a href="#" @click.prevent="goToAllNews" :alvqa="alvqa + '-link-show-all-news'">{{ $t('lang_GENERIC_CAPTION_SHOW_ALL') }}</a>
                    </v-card-actions>
                </v-card>

                <!-- Dynamic Video from Storyblok - Only show if loading or content available -->
                <v-card 
                    v-if="isLoadingVideos || storyblokVideoContent"
                    elevation="0" 
                    class="mb-3" 
                    style="background-color: #F2EDDA;"
                >
                    <!-- Loading state -->
                    <template v-if="isLoadingVideos">
                        <v-card-subtitle>{{ $t('lang_GENERIC_CAPTION_LOADING') }}</v-card-subtitle>
                    </template>
                    
                    <!-- Show video content if available -->
                    <template v-else-if="storyblokVideoContent">
                        <v-card-title 
                            :style="getHeadlineStyle(storyblokVideoContent.content.Priority)"
                        >{{ storyblokVideoContent.content.Headline }}</v-card-title>
                        <v-card-text>
                            <iframe 
                                v-if="getYouTubeEmbedUrl(storyblokVideoContent.content.YouTubeLink)"
                                width="100%" 
                                height="315" 
                                :src="getYouTubeEmbedUrl(storyblokVideoContent.content.YouTubeLink)" 
                                :title="storyblokVideoContent.content.Headline"
                                frameborder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                referrerpolicy="strict-origin-when-cross-origin" 
                                allowfullscreen
                            />
                        </v-card-text>
                    </template>
                </v-card>
            </v-col>

            <!-- Column 3: Important Updates -->
            <v-col cols="3">
                <!-- Data Sharing (highest priority - at top) -->
                <div v-if="notificationsStack.length" class="mb-3">
                    <DataSharingNotificationCard :notifications="notificationsStack" title="Data sharing"/>
                </div>
                
                <!-- Running Tasks (live progress) -->
                <v-card v-if="runningTasks.length" elevation="0" class="mb-3">
                    <v-card-title class="pl-4 pb-2">{{ $t('lang_DASHBOARD_CAPTION_CURRENT_PROCESSES') }}</v-card-title>
                    <div v-for="task in runningTasks" :key="task.taskId" class="pl-4 pr-4 pb-3">
                        <div class="font-weight-medium mb-1">
                            {{ $t(task.title) || task.title }}
                        </div>
                        <v-progress-linear
                            :value="task.progress || 0"
                            :buffer-value="Math.min((task.progress || 0) + 15, 100)"
                            color="primary"
                            stream
                            height="8"
                            rounded
                        ></v-progress-linear>
                        <p class="mt-1 mb-0 text-caption grey--text">
                            {{ task.progress || 0 }}% {{ task.status ? '- ' + task.status : '' }}
                        </p>
                    </div>
                </v-card>

                <!-- Task Notifications (background process completions) -->
                <v-card v-if="taskNotifications.length" elevation="0" class="mb-3">
                    <v-card-title class="pl-4 pb-2">{{ $t('lang_DASHBOARD_CAPTION_CURRENT_PROCESSES') }}</v-card-title>
                    <div v-for="task in taskNotifications" :key="task.id" class="pl-4 pr-4 pb-3">
                        <div class="font-weight-medium">
                            {{ $t(task.title) || task.title }}
                        </div>
                        <div v-for="(download, idx) in task.downloads" :key="idx">
                            <a href="#" @click.prevent="downloadAndDismiss(download.path, task.id)">
                                {{ $t(download.label) || download.label }}
                            </a>
                        </div>
                    </div>
                </v-card>
                
                <!-- Reminders (max 3) -->
                <div v-if="contractsRemindersList.length" class="mb-3">
                    <BaseReminderCard :reminders="contractsRemindersList"  title="lang_AZ_COMMUNICATION_LIST_RECONTACT_TITLE" />
                </div>
                <div v-if="azRemindersList.length" class="mb-3">
                    <BaseReminderCard :reminders="azRemindersList"  title="lang_AZ_COMMUNICATION_LIST_TITLE" />
                </div>
            </v-col>
        </v-row>
        
        <!-- Image Dialog -->
        <v-dialog 
            v-model="showImageDialog" 
            max-width="90vw"
            content-class="image-dialog"
        >
            <v-card>
                <v-card-actions class="justify-end pa-2">
                    <v-btn 
                        icon 
                        @click="showImageDialog = false"
                        color="white"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-actions>
                <v-img 
                    v-if="getCurrentImageForDialog()"
                    :src="getCurrentImageForDialog().filename"
                    :alt="getCurrentImageForDialog().alt || getCurrentHeadlineForDialog()"
                    contain
                    max-height="80vh"
                />
            </v-card>
        </v-dialog>
    </v-container>
</template>
<script>
import GemaWorksCheckDialog from '@/components/_dialogs/GemaWorksCheckDialog'
import GemaSoundfileDialog from '@/pages/2_publishing/1_works/_dialogs/GemaSoundfileDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import {    DropdownDashboardTagExportDomains,
            DropdownDashboardCsvConversionTools,
} from '@/assets/dropdowns/alvDropdowns.js'
import BaseReminderCard from '@/components/_base/BaseReminderCard.vue'
import DataSharingNotificationCard from './_cards/DataSharingNotificationCard.vue'
import { richTextResolver, BlockTypes } from '@storyblok/richtext'

import api from '@/api_alv'
import { sortByMappingsDomain, sortByStoryblokDomain } from '@/utils/navigationOrder'
import { openCalendlyPopup, CALENDLY_USER_MEETING_URLS } from '@/utils/calendly'

export default {
    name: 'Dashboard',
    components: {
        GemaWorksCheckDialog,
        GemaSoundfileDialog,
        BaseButton,
        BaseEditPageDropdown,
        BaseReminderCard,
        DataSharingNotificationCard
    },
    data() {
        return {
            userLoginId:  this.$store.getters["getUserInformation"].id,
            alvqa: 'dashboard',
            context: 'Dashboard',
            fileObj: null,
            formData: new FormData(),
            filePath: '',
            csvConversionInProgress: false,
            storyblokContent: null,
            noInterestsStoryContent: null,
            showNoInterestsMessage: false,
            isLoadingInsights: true,
            showImageDialog: false,
            storyblokVideoContent: null,
            showNoVideoInterestsMessage: false,
            isLoadingVideos: true,
            selectedQuicklinksGuide: null,
            selectedQuicklinksTemplate: null,
        }
    },
    watch: {
        // Watch for language changes and refetch insights and videos
        '$i18n.locale'() {
            // Reset loading states immediately
            this.isLoadingInsights = true
            this.isLoadingVideos = true
            
            // Fetch with slight delay to avoid race conditions
            this.$nextTick(() => {
                this.fetchStoryblokInsights()
                this.fetchStoryblokVideoContent()
                this.fetchQuicklinksTemplates()
                this.fetchQuicklinksGuides()
            })
        }
    },
    computed: {
        showGemaWorksCheckDialog: {
            get() { return this.$store.getters.getShowGemaWorksCheckDialog },
            set(showGemaWorksCheckDialog) { this.$store.commit('setShowGemaWorksCheckDialog', showGemaWorksCheckDialog) }
        },
        isLaunchMonth() {
            var today = new Date().getTime()
            var from = new Date("07/01/2021").getTime()
            var to = new Date("07/31/2021").getTime()
            return (today >= from && today <= to)
        },

        tagExportDomainItems() { return DropdownDashboardTagExportDomains },
        
        csvConversionToolsItems() {
            const userAccountId = String(this.$store.getters["getUserInformation"].accountId || '')
            return DropdownDashboardCsvConversionTools.filter(item => {
                if (!item.restrictToAccountIds) return true
                return item.restrictToAccountIds.includes(userAccountId)
            })
        },

        quicklinksGuidesItems() {
            const guides = this.$store.getters.getQuicklinksGuides || []
            return sortByStoryblokDomain(guides)
                .filter(g => g.pdfUrl)
                .map(g => ({
                    text: g.headline,
                    value: g.pdfUrl,
                    alvqa: 'dropdown-quicklinks-guide-' + (g.pdfName || g.headline),
                }))
        },

        quicklinksTemplatesItems() {
            const templates = this.$store.getters.getQuicklinksTemplates || []
            // Only show ALVDIGITAL's own entity templates (Werke, VÖ/Alben, A-Z).
            // Third-party formats (e.g. GEMA v8) may contain real customer data in
            // their original file slot (used internally for testing/import) and must
            // not be exposed via Quicklinks.
            const filtered = templates.filter(t => t.title && t.title.includes('ALV'))
            return sortByMappingsDomain(filtered)
                .map(t => ({
                    text: t.title,
                    value: t.alvId,
                    alvqa: 'dropdown-quicklinks-template-' + t.alvId,
                }))
        },

        quicklinksGuidesLabel() {
            const count = this.quicklinksGuidesItems.length
            const label = this.$t('lang_DASHBOARD_CAPTION_QUICKLINKS_GUIDES')
            return count ? `${label} (${count})` : label
        },

        quicklinksTemplatesLabel() {
            const count = this.quicklinksTemplatesItems.length
            const label = this.$t('lang_DASHBOARD_CAPTION_QUICKLINKS_TEMPLATES')
            return count ? `${label} (${count})` : label
        },

        selectedToolConfig() {
            const tool = this.$store.getters.getDashboardDetailsSelectedCsvConversionTool
            return tool ? this.csvConversionToolsItems.find(item => item.value === tool) : null
        },

        getAcceptedFileTypes() {
            if (!this.selectedToolConfig || !this.selectedToolConfig.validation) return ''
            return this.selectedToolConfig.validation.map(ext => `.${ext.toLowerCase()}`).join(',')
        },

        contractsRemindersList() {
            const publishingContracts = this.$store.getters["getPublishingReminders"];
            const labelContracts = this.$store.getters["getLabelReminders"];
            const getValidArray = (arr) => Array.isArray(arr) && arr.length ? arr : [];
            const validReminders = [
                ...getValidArray(publishingContracts),
                ...getValidArray(labelContracts)
            ]
            const sortedReminders = validReminders.sort((a, b) => new Date(a.reminderDate) - new Date(b.reminderDate)); 
            const topReminders = sortedReminders.slice(0, 10);

            return topReminders;
        },
        azRemindersList() {
           const azReminders = this.$store.getters["getAzReminders"];
           
           return azReminders
        },
        notificationsStack() {
            return this.$store.getters.getDataSharingNotificationsList
        },
        taskNotifications() {
            return this.$store.getters.getTaskNotifications
        },
        runningTasks() {
            return this.$store.getters.getRunningTasks
        }
    },
    methods: {
        goToAllNews() {
            this.$router.push('/servicecenter/insights')
        },

        openUserMeeting() {
            openCalendlyPopup(CALENDLY_USER_MEETING_URLS, this.$i18n.locale)
        },

        onQuicklinksGuideSelected(value) {
            if (!value) return
            window.open(value, '_blank', 'noopener,noreferrer')
            this.$nextTick(() => { this.selectedQuicklinksGuide = null })
        },

        fetchQuicklinksGuides() {
            const currentLanguage = this.$i18n.locale || 'de'
            this.$store.dispatch('fetchQuicklinksGuides', currentLanguage)
        },

        async onQuicklinksTemplateSelected(value) {
            if (!value) return
            try {
                await api.downloadQuicklinksTemplate(value)
                this.$store.commit('setSnackbar', {
                    type: 'success',
                    msg: this.$t('lang_GENERAL_SNACKBAR_DOWNLOAD_SUCCESSFULL')
                })
            } catch (error) {
                this.$store.commit('setSnackbar', {
                    type: 'error',
                    msg: this.$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
                })
            } finally {
                this.$nextTick(() => { this.selectedQuicklinksTemplate = null })
            }
        },

        async fetchQuicklinksTemplates() {
            try {
                await this.$store.dispatch('fetchQuicklinksTemplates', this.$i18n.locale)
            } catch (error) {
                // silent fail — dropdown stays empty
            }
        },

        launchTagsExportByDomain() {
            this.$store.dispatch('tagsExport')
        },

        validateFileExtension(file) {
            if (!file) return false
            const ext = file.name.split('.').pop().toUpperCase()
            const tool = this.$store.getters.getDashboardDetailsSelectedCsvConversionTool
            const toolConfig = this.csvConversionToolsItems.find(item => item.value === tool)
            return toolConfig && toolConfig.validation && toolConfig.validation.includes(ext)
        },

        async submitFile(file) {
            if (!file) {
                this.filePath = ''
                return
            }

            if (!this.validateFileExtension(file)) {
                this.$store.commit('setSnackbar', {
                    text: this.$t('lang_DASHBOARD_ERROR_INVALID_FILE_TYPE'),
                    color: 'error'
                })
                this.fileObj = null
                return
            }

            this.$store.commit('setIsLoading', true)
            const fd = new FormData()
            fd.append('file', file)

            const tool = this.$store.getters.getDashboardDetailsSelectedCsvConversionTool
            const params = `?action=csvConversionTools&type=${encodeURIComponent(tool)}`

            const resp = await api.putFile('dashboard', fd, params)
            this.$store.commit('setIsLoading', false)

            if (resp.error === -1) {
                // 'txt' holds the uploaded-file path on the server
                this.filePath = resp.response.txt
            } else {
                this.filePath = ''
            }
        },

        async launchCsvConversionTool() {
            this.csvConversionInProgress = true;
            try {
                // 1) fire off the conversion + download
                await this.$store.dispatch('csvConversionTool', this.filePath);
            } finally {
                this.csvConversionInProgress = false;
                // 2) reset the dropdown (tool) and file input
                this.fileObj = null;
                this.filePath = '';
                this.$store.commit(
                    'setDashboardDetailsSelectedCsvConversionTool',
                    ''
                );
            }
        },

        async initializeReminders() {
            await Promise.all([
                this.$store.dispatch('fetchPublishingReminders'),
                this.$store.dispatch('fetchAZReminders'),
                this.$store.dispatch('fetchLabelReminders')
            ]);
        },
        handleNewNotification(notification) {
            this.$store.commit('setDataSharingNotifications', notification)
        },
        handleTaskNotification(taskData) {
            // Show snackbar with task completion message
            const title = this.$t(taskData.title) || taskData.title
            const message = this.$t(taskData.message) || taskData.message
            
            if (taskData.type === 'task_complete') {
                this.$store.commit('setSnackbar', {
                    type: 'success',
                    msg: `${title}: ${message}`
                })
            } else if (taskData.type === 'task_failed') {
                this.$store.commit('setSnackbar', {
                    type: 'error',
                    msg: `${title}: ${message}`
                })
            }
        },
        downloadTaskFile(path) {
            if (path) {
                // Use window.open to avoid CORS issues with direct file URLs
                window.open(path, '_blank')
            }
        },
        downloadAndDismiss(path, taskId) {
            this.downloadTaskFile(path)
            this.$store.commit('removeTaskNotification', taskId)
        },
        dismissTaskNotification(taskId) {
            this.$store.commit('removeTaskNotification', taskId)
        },
        async initializeNotifications() {
            await this.$store.dispatch('fetchExistingNotifications')

            const connectionResult = await this.$store.dispatch('fetchAblyRequestToken')
        
            if (connectionResult) {
                // Data Sharing notifications (between ALV accounts)
                this.$store.dispatch('subscribeToAblyNotifications', (notification) => {
                    this.handleNewNotification(notification.data)
                });
                
                // Task notifications are now initialized globally in router.js
            }
        },

        async fetchStoryblokInsights() {
            try {
                this.isLoadingInsights = true
                const currentLanguage = this.$i18n.locale || 'de'
                const response = await this.$store.dispatch('fetchDashboardInsights', { language: currentLanguage, dateLimitWeeks: 8 })
                
                if (response) {
                    if (response.noInterests) {
                        // User has no interests configured
                        this.showNoInterestsMessage = true
                        this.storyblokContent = null
                        this.noInterestsStoryContent = response.story // Store the specific no interests story
                    } else if (response.story) {
                        // User has interests and we found content
                        this.storyblokContent = response.story
                        this.showNoInterestsMessage = false
                        this.noInterestsStoryContent = null
                    } else {
                        // User has interests but no content available in this language
                        this.storyblokContent = null
                        this.showNoInterestsMessage = false
                        this.noInterestsStoryContent = null
                    }
                } else {
                    this.storyblokContent = null
                    this.showNoInterestsMessage = false
                    this.noInterestsStoryContent = null
                }
            } catch (error) {

                this.storyblokContent = null
                this.showNoInterestsMessage = false
                this.noInterestsStoryContent = null
            } finally {
                this.isLoadingInsights = false
            }
        },

        async fetchStoryblokVideoContent() {
            try {
                this.isLoadingVideos = true
                const currentLanguage = this.$i18n.locale || 'de'
                const response = await this.$store.dispatch('fetchDashboardVideos', currentLanguage)
                
                if (response) {
                    if (response.noInterests) {
                        // User has no interests configured
                        this.showNoVideoInterestsMessage = true
                        this.storyblokVideoContent = null
                    } else if (response.story) {
                        // User has interests and we found video content
                        this.storyblokVideoContent = response.story
                        this.showNoVideoInterestsMessage = false
                    } else {
                        // User has interests but no video content available in this language
                        this.storyblokVideoContent = null
                        this.showNoVideoInterestsMessage = false

                    }
                } else {
                    this.storyblokVideoContent = null
                    this.showNoVideoInterestsMessage = false
                }
            } catch (error) {

                this.storyblokVideoContent = null
                this.showNoVideoInterestsMessage = false
            } finally {
                this.isLoadingVideos = false
            }
        },

        renderRichText(richTextObject) {
            if (!richTextObject) return ''
            
            // Configure the official Storyblok rich text renderer for Vue 2 (HTML string output)
            const { render } = richTextResolver({
                resolvers: {
                    // Handle emojis properly - using string key instead of constant
                    'emoji': (node) => {
                        const emoji = node.attrs && node.attrs.emoji ? node.attrs.emoji : ''
                        return emoji
                    },
                    // Customize table rendering to maintain existing styles
                    [BlockTypes.TABLE]: (node) => {
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<table class="dashboard-table">${children}</table>`
                    },
                    [BlockTypes.TABLE_ROW]: (node) => {
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<tr>${children}</tr>`
                    },
                    [BlockTypes.TABLE_CELL]: (node) => {
                        let styleAttr = ''
                        let colspanAttr = ''
                        let rowspanAttr = ''
                        let colwidthAttr = ''
                        
                        if (node.attrs && node.attrs.backgroundColor) {
                            styleAttr = ` style="background-color: ${node.attrs.backgroundColor}"`
                        }
                        if (node.attrs && node.attrs.colspan) {
                            colspanAttr = ` colspan="${node.attrs.colspan}"`
                        }
                        if (node.attrs && node.attrs.rowspan) {
                            rowspanAttr = ` rowspan="${node.attrs.rowspan}"`
                        }
                        if (node.attrs && node.attrs.colwidth) {
                            colwidthAttr = ` data-colwidth="${node.attrs.colwidth}"`
                        }
                        
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<td${styleAttr}${colspanAttr}${rowspanAttr}${colwidthAttr}>${children}</td>`
                    },
                    [BlockTypes.TABLE_HEADER]: (node) => {
                        let styleAttr = ''
                        let colspanAttr = ''
                        let rowspanAttr = ''
                        let colwidthAttr = ''
                        
                        if (node.attrs && node.attrs.backgroundColor) {
                            styleAttr = ` style="background-color: ${node.attrs.backgroundColor}"`
                        }
                        if (node.attrs && node.attrs.colspan) {
                            colspanAttr = ` colspan="${node.attrs.colspan}"`
                        }
                        if (node.attrs && node.attrs.rowspan) {
                            rowspanAttr = ` rowspan="${node.attrs.rowspan}"`
                        }
                        if (node.attrs && node.attrs.colwidth) {
                            colwidthAttr = ` data-colwidth="${node.attrs.colwidth}"`
                        }
                        
                        const children = Array.isArray(node.children) ? node.children.join('') : node.children
                        return `<th${styleAttr}${colspanAttr}${rowspanAttr}${colwidthAttr}>${children}</th>`
                    }
                }
            })
            
            try {
                return render(richTextObject)
            } catch (error) {

                return ''
            }
        },

        getHeadlineStyle(priority) {
            const baseStyle = 'word-break: keep-all; hyphens: none;'
            
            if (priority === 'critical') {
                return baseStyle + ' color: #800020 !important;'  // Burgundy red for critical
            }
            
            return baseStyle  // Normal styling for normal and top
        },

        getYouTubeEmbedUrl(watchUrl) {
            if (!watchUrl) return null
            
            // Convert watch URL to embed URL
            // From: https://www.youtube.com/watch?v=2DnB5mKb6...
            // To: https://www.youtube.com/embed/2DnB5mKb6...
            
            const videoIdMatch = watchUrl.match(/[?&]v=([^&]+)/)
            if (videoIdMatch && videoIdMatch[1]) {
                return `https://www.youtube.com/embed/${videoIdMatch[1]}?rel=0`
            }
            
            return null
        },

        getCurrentImageForDialog() {
            if (this.storyblokContent && this.storyblokContent.content.Image && this.storyblokContent.content.Image.filename) {
                return this.storyblokContent.content.Image
            }
            if (this.noInterestsStoryContent && this.noInterestsStoryContent.content.Image && this.noInterestsStoryContent.content.Image.filename) {
                return this.noInterestsStoryContent.content.Image
            }
            return null
        },

        getCurrentHeadlineForDialog() {
            if (this.storyblokContent && this.storyblokContent.content.Headline) {
                return this.storyblokContent.content.Headline
            }
            if (this.noInterestsStoryContent && this.noInterestsStoryContent.content.Headline) {
                return this.noInterestsStoryContent.content.Headline
            }
            return ''
        }
    },
    async created() {
        this.$store.commit('setDashboardDetailsSelectedTagExportDomain', 'az-persons');
        await this.initializeReminders();
        this.initializeNotifications();
        this.fetchStoryblokInsights();
        this.fetchStoryblokVideoContent();
        this.fetchQuicklinksTemplates();
        this.fetchQuicklinksGuides();
    }
}
</script>

<style>
/* Font size adjustments scoped to the dashboard page only */
.dashboard-page .v-card__title {
    font-size: 16px !important;
    font-weight: 500 !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
    line-height: 1.4 !important;
}

.dashboard-page .v-card__subtitle {
    font-size: 15px !important;
    line-height: 1.4 !important;
}

.dashboard-page .v-card__text {
    font-size: 15px !important;
    line-height: 1.5 !important;
}

.dashboard-page .v-card__text p {
    font-size: 15px !important;
    margin-bottom: 6px !important;
}

.dashboard-page .v-card__text ul {
    font-size: 15px !important;
}

.dashboard-page .v-card__text li {
    font-size: 15px !important;
    margin-bottom: 4px !important;
}

/* Form elements in cards */
.dashboard-page .v-card .v-text-field .v-label {
    font-size: 13px !important;
}

.dashboard-page .v-card .v-select .v-label {
    font-size: 13px !important;
}

.dashboard-page .v-card .v-input__slot {
    font-size: 14px !important;
}

.dashboard-page .v-card .v-select .v-select__selection {
    font-size: 14px !important;
}

/* Button text */
.dashboard-page .v-card .v-btn {
    font-size: 12px !important;
}



/* Target BaseReminderCard elements specifically for tight line height */
.dashboard-page .v-card__subtitle.py-0.font-weight-medium {
    line-height: 1.2 !important;
    margin-bottom: 2px !important;
    padding-top: 4px !important;
    padding-bottom: 0px !important;
}

.dashboard-page .v-card__text.pb-0 {
    line-height: 1.2 !important;
    padding-top: 0px !important;
    padding-bottom: 0px !important;
}

.dashboard-page .v-card__text.pb-0 p {
    line-height: 1.2 !important;
    margin-bottom: 2px !important;
}

/* Clickable image styling */
.dashboard-page .clickable-image {
    cursor: pointer !important;
    transition: opacity 0.2s ease !important;
}

.dashboard-page .clickable-image:hover {
    opacity: 0.8 !important;
}

/* Image dialog styling */
.image-dialog .v-card {
    background: rgba(0, 0, 0, 0.9) !important;
}

.image-dialog .v-card__actions {
    position: absolute !important;
    top: 0 !important;
    right: 0 !important;
    z-index: 1 !important;
    background: transparent !important;
}

/* Quicklinks card */
.dashboard-page .quicklinks-card {
    background-color: #F2EDDA;
}

.dashboard-page .quicklinks-avatar {
    float: right;
    width: 80px;
    height: 80px;
    margin: 12px 12px 4px 8px;
    object-fit: contain;
}

/* Scoped to the quicklinks templates dropdown menu (menu is portaled outside the card) */
.quicklinks-template-menu .v-list-item__title {
    font-weight: 400 !important;
}

/* Table styling for Storyblok content */
.dashboard-page .v-card__text .dashboard-table {
    width: 100% !important;
    border-collapse: collapse !important;
    margin: 10px 0 !important;
    font-size: 13px !important;
}

.dashboard-page .v-card__text .dashboard-table th,
.dashboard-page .v-card__text .dashboard-table td {
    border: 1px solid #ddd !important;
    padding: 8px 12px !important;
    text-align: left !important;
    vertical-align: top !important;
    font-size: 13px !important;
}

.dashboard-page .v-card__text .dashboard-table p {
    font-size: 13px !important;
    margin-bottom: 0 !important;
    line-height: 1.4 !important;
}

.dashboard-page .v-card__text .dashboard-table th {
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
    font-size: 13px !important;
}

.dashboard-page .v-card__text .dashboard-table td {
    background-color: #ffffff !important;
}
</style>
